import React, { useState, useEffect, useRef } from "react";
import {
  X,
  ShoppingCart,
  Info,
  GripVertical,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

export interface SelectedItem {
  id: string;
  displayName?: string;
  name?: string;
  price: number;
  category?: "DIRECT" | "AI" | "LIFECYCLE";
}

interface SelectedFeaturesFloatingProps {
  selectedItems: SelectedItem[];
  onRemove: (id: string) => void;
  totalPrice: string;
  billingType: "Monthly" | "Quarterly" | "Yearly";
  title?: string;
}

const SelectedFeaturesFloating: React.FC<SelectedFeaturesFloatingProps> = ({
  selectedItems,
  onRemove,
  totalPrice,
  billingType,
  title = "Selected Items",
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initial position: center vertically on the right
    const initialY = window.innerHeight * 0.15;
    setPosition({ x: 0, y: initialY });
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = window.innerWidth - e.clientX - (320 - dragOffset.x); // 320 is w-80
        const newY = e.clientY - dragOffset.y;
        setPosition({ x: Math.max(0, newX), y: Math.max(0, newY) });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  if (selectedItems.length === 0) return null;

  return (
    <div
      ref={containerRef}
      style={{
        right: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? "grabbing" : "auto",
      }}
      className="fixed z-[1000] w-80 bg-white/90 backdrop-blur-lg rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-white/20 overflow-hidden animate-in fade-in slide-in-from-right-10 flex flex-col max-h-[75vh]"
    >
      <div
        onMouseDown={handleMouseDown}
        onClick={() => !isDragging && setIsCollapsed(!isCollapsed)}
        className="bg-gradient-to-r from-primary to-primary p-4 text-white flex items-center justify-between shadow-md cursor-grab active:cursor-grabbing transition-colors"
      >
        <div className="flex items-center gap-2 pointer-events-none">
          <GripVertical className="w-4 h-4 text-white/50" />
          <div className="bg-white/20 p-1.5 rounded-lg">
            <ShoppingCart className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-medium text-sm leading-none">{title}</h3>
            <p className="text-[10px] text-blue-100 mt-1 uppercase tracking-wider font-medium">
              {isCollapsed ? "Click to expand" : "Checkout Progress"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-white text-blue-700 px-2 py-1 rounded-full text-xs font-medium min-w-[24px] text-center">
            {selectedItems.length}
          </div>
          {isCollapsed ? (
            <ChevronDown className="w-4 h-4 text-white/70" />
          ) : (
            <ChevronUp className="w-4 h-4 text-white/70" />
          )}
        </div>
      </div>

      {!isCollapsed && (
        <>
          <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
            <div className="space-y-4">
              {selectedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start justify-between gap-3 group bg-blue-50/50 p-3 rounded-xl border border-blue-50 hover:border-blue-200 transition-all"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 font-medium truncate leading-tight">
                      {item.displayName || item.name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <p className="text-[13px] text-gray-800 font-medium">
                        {item.price > 0
                          ? `${item.price.toLocaleString()}`
                          : "Included"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(item.id);
                    }}
                    className="text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all p-1.5 rounded-lg"
                    title="Remove item"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="p-5 bg-white border-t border-blue-50 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-500 text-sm font-medium">
                Estimated Total
              </span>
              <div className="text-right">
                <div className="flex items-baseline gap-1">
                  <span className="text-xs font-medium text-gray-900 mb-1">
                    NGN
                  </span>
                  <span className="text-2xl font-medium text-gray-900 tracking-tight">
                    {totalPrice}
                  </span>
                </div>
                {/* <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest -mt-1">
                  Billed {billingType}
                </p> */}
              </div>
            </div>
            <div className="mt-4 text-[10px] text-center text-gray-400 italic">
              *Prices exclude applicable taxes and setup fees
            </div>
          </div>
        </>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
      `}</style>
    </div>
  );
};

export default SelectedFeaturesFloating;
